import { useUserContext } from 'common/contexts/UserContext';
import React from 'react';

import { SegmentService } from '../../services/helpers/segment';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Properties = Record<string, any>;

/**
 * Provides access to the segment in a modern, hook-friendly way
 * @returns a tuple containing trackEvent and trackPage functions
 */
export const useSegment = () => {
  const { user } = useUserContext();

  /**
   * Passthrough for SegmentService.trackEvent
   * DEPRECATED: Favor `track`
   */
  const trackEvent = React.useCallback(
    (eventTitle: string, properties?: Properties, options?: SegmentAnalytics.SegmentOpts) => {
      SegmentService.trackEvent(eventTitle, properties, options);
    },
    []
  );

  /**
   * DEPRECATED: Favor `track`
   */
  const trackEventNoSlack = React.useCallback(
    (eventTitle: string, properties?: Properties, options?: SegmentAnalytics.SegmentOpts) => {
      SegmentService.trackEventNoSlack(eventTitle, properties, options);
    },
    []
  );

  const track = React.useCallback(
    (
      eventTitle: string,
      properties?: Properties,
      options?: Partial<{
        includeUser: boolean;
        includeCompany: boolean;
        slack: boolean;
        segmentOptions: SegmentAnalytics.SegmentOpts;
      }>
    ) => {
      let payload: Properties = { ...properties };
      if (options?.includeUser) {
        payload = {
          email: user?.email || '',
          firstName: user?.first_name || '',
          lastName: user?.last_name || '',
          phoneNumber: user?.phone_number || '',
          companyRole: user?.company_role || '',
          ...payload,
        };
      }

      if (options?.includeCompany) {
        payload = {
          companyType: user?.company?.company_type_detail || '',
          companyName: user?.company?.name || '',
          autoJoinEmailDomain: user?.company?.auto_join_email_domain || false,
          scac: user?.company?.scac || '',
          otherType: user?.company?.other_type || '',
          expectedContainerVolume: user?.company?.expected_container_volume || 0,
          portCollectionCodes: user?.company?.port_collections || [],
          ...payload,
        };
      }

      if (options?.slack) {
        SegmentService.trackEvent(eventTitle, payload, options?.segmentOptions);
      } else {
        SegmentService.trackEventNoSlack(eventTitle, payload, options?.segmentOptions);
      }
    },
    [user]
  );

  /**
   * Passthrough for SegmentService.trackPage
   */
  const trackPage = React.useCallback((pathname: string) => {
    SegmentService.trackPage(pathname);
  }, []);

  return { trackEvent, trackEventNoSlack, track, trackPage };
};
